import { useEffect, useMemo, useState } from "react";
import { X, TrendingUp, Users, MapPin, Package, BarChart3 } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { fmtSf } from "../lib/utils";
import { toast } from "sonner";
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from "recharts";

function fmt(n) {
  if (n === null || n === undefined || isNaN(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1_000_000) return (n / 1_000_000).toFixed(2) + "M";
  if (a >= 1000) return (n / 1000).toFixed(2) + "K";
  return Number(n).toLocaleString();
}
function fmtMoney(n) {
  if (n === null || n === undefined || isNaN(n)) return "$0";
  return `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const CONFIGS = {
  hours: { title: "Total Hours · Breakdown", icon: <TrendingUp size={16} /> },
  overtime: { title: "Overtime · Breakdown", icon: <TrendingUp size={16} /> },
  production: { title: "Production SF · Breakdown", icon: <BarChart3 size={16} /> },
  material: { title: "Material Cost · Breakdown", icon: <Package size={16} /> },
  pieces: { title: "Pieces · By Bed", icon: <MapPin size={16} /> },
};

export default function DeptDrillModal({ open, onClose, dept, kind, label, params, accent = "#00D4FF" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !dept || !kind) return;
    let cancelled = false;
    const run = async () => {
      setLoading(true); setData(null);
      try {
        const r = await api.get(`/departments/${dept}/drill/${kind}`, { params });
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [open, dept, kind, params]);

  // Escape key closes modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const cfg = CONFIGS[kind] || {};
  const title = label || cfg.title || "Breakdown";

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-[2px] animate-in fade-in duration-150"
        onClick={onClose}
        data-testid="dept-drill-backdrop"
      />
      {/* Right-side drawer */}
      <aside
        data-testid="dept-drill-modal"
        role="dialog"
        aria-modal="true"
        className="fixed right-0 top-0 bottom-0 z-50 w-full sm:w-[640px] lg:w-[780px] xl:w-[880px] bg-[#12141D] border-l border-border shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
      >
        <header className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0 bg-[#12141D]">
          <div className="flex items-center gap-3">
            <span style={{ color: accent }}>{cfg.icon}</span>
            <h2 className="font-heading text-xl font-bold">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-white p-1"
            data-testid="close-drill-modal"
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </header>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {loading && <div className="text-sm text-muted-foreground py-12 text-center uppercase tracking-widest">Loading…</div>}
          {!loading && data && (
            <>
              {kind === "hours" && <HoursDrill data={data} accent={accent} />}
              {kind === "overtime" && <OvertimeDrill data={data} accent={accent} />}
              {kind === "production" && <ProductionDrill data={data} accent={accent} />}
              {kind === "material" && <MaterialDrill data={data} accent={accent} />}
              {kind === "pieces" && <PiecesDrill data={data} accent={accent} />}
            </>
          )}
        </div>
      </aside>
    </>
  );
}

function ChartCard({ title, children, icon }) {
  return (
    <div className="bg-[#0A0B10] border border-border p-4">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-muted-foreground mb-3">
        {icon} {title}
      </div>
      {children}
    </div>
  );
}

const tooltipStyle = {
  backgroundColor: "#0A0B10",
  border: "1px solid #1F2937",
  fontSize: 12,
};

function HoursDrill({ data, accent }) {
  const weeks = data.weekly || [];
  return (
    <div className="space-y-5">
      <ChartCard title="Weekly Hours (Regular + Overtime)" icon={<BarChart3 size={12} />}>
        <ResponsiveContainer width="100%" height={260} minWidth={0} minHeight={0}>
          <BarChart data={weeks} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid stroke="#1F2937" vertical={false} />
            <XAxis dataKey="week" stroke="#94A3B8" tick={{ fontSize: 10 }} />
            <YAxis stroke="#94A3B8" tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="regular" stackId="h" fill={accent} name="Regular" />
            <Bar dataKey="overtime" stackId="h" fill="#F87171" name="Overtime" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Top Employees by Total Hours" icon={<Users size={12} />}>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <tr>
              <th className="text-left py-2">Employee</th>
              <th className="text-right py-2">Regular</th>
              <th className="text-right py-2">OT</th>
              <th className="text-right py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.top_employees?.map((e, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="py-2 font-mono" style={{ color: accent }}>{e.employee}</td>
                <td className="py-2 font-mono text-right">{fmt(e.regular)}</td>
                <td className="py-2 font-mono text-right text-red-300">{fmt(e.overtime)}</td>
                <td className="py-2 font-mono text-right">{fmt(e.hours)}</td>
              </tr>
            ))}
            {!data.top_employees?.length && (
              <tr><td colSpan={4} className="py-6 text-center text-muted-foreground text-xs uppercase">No data</td></tr>
            )}
          </tbody>
        </table>
      </ChartCard>
    </div>
  );
}

function OvertimeDrill({ data, accent }) {
  const weeks = data.weekly || [];
  return (
    <div className="space-y-5">
      <ChartCard title="Weekly Overtime % Trend" icon={<TrendingUp size={12} />}>
        <ResponsiveContainer width="100%" height={260} minWidth={0} minHeight={0}>
          <LineChart data={weeks} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid stroke="#1F2937" vertical={false} />
            <XAxis dataKey="week" stroke="#94A3B8" tick={{ fontSize: 10 }} />
            <YAxis stroke="#94A3B8" tick={{ fontSize: 10 }} tickFormatter={(v) => `${v}%`} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${v}%`} />
            <Line type="monotone" dataKey="pct" stroke="#FBBF24" strokeWidth={2} dot={{ fill: "#FBBF24", r: 3 }} name="OT %" />
          </LineChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Top Employees by Overtime" icon={<Users size={12} />}>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <tr>
              <th className="text-left py-2">Employee</th>
              <th className="text-right py-2">OT Hours</th>
              <th className="text-right py-2">Total Hours</th>
              <th className="text-right py-2">OT %</th>
            </tr>
          </thead>
          <tbody>
            {data.top_employees?.map((e, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="py-2 font-mono" style={{ color: accent }}>{e.employee}</td>
                <td className="py-2 font-mono text-right text-red-300">{fmt(e.overtime)}</td>
                <td className="py-2 font-mono text-right">{fmt(e.total)}</td>
                <td className={`py-2 font-mono text-right ${e.pct > 10 ? "text-amber-400" : "text-emerald-400"}`}>
                  {e.pct}%
                </td>
              </tr>
            ))}
            {!data.top_employees?.length && (
              <tr><td colSpan={4} className="py-6 text-center text-muted-foreground text-xs uppercase">No data</td></tr>
            )}
          </tbody>
        </table>
      </ChartCard>
    </div>
  );
}

function ProductionDrill({ data, accent }) {
  const weeks = data.weekly || [];
  return (
    <div className="space-y-5">
      <ChartCard title="Weekly Production SF" icon={<BarChart3 size={12} />}>
        <ResponsiveContainer width="100%" height={260} minWidth={0} minHeight={0}>
          <BarChart data={weeks} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
            <CartesianGrid stroke="#1F2937" vertical={false} />
            <XAxis dataKey="week" stroke="#94A3B8" tick={{ fontSize: 10 }} />
            <YAxis stroke="#94A3B8" tick={{ fontSize: 10 }} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="sf" fill={accent} name="SF" />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Top Beds by Production SF" icon={<MapPin size={12} />}>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <tr>
              <th className="text-left py-2">Bed</th>
              <th className="text-left py-2">Jobs</th>
              <th className="text-right py-2">Pieces</th>
              <th className="text-right py-2">Total SF</th>
            </tr>
          </thead>
          <tbody>
            {data.top_beds?.map((b, i) => (
              <tr key={i} className="border-t border-border/40 align-top">
                <td className="py-2 font-mono whitespace-nowrap" style={{ color: accent }}>{b.bed}</td>
                <td className="py-2 font-mono text-xs">
                  {(b.jobs && b.jobs.length)
                    ? <div className="flex flex-wrap gap-1">
                        {b.jobs.map((j) => (
                          <span key={j} className="px-1.5 py-0.5 border border-border/60 bg-[#0A0B10] text-[10px] text-muted-foreground">
                            {j}
                          </span>
                        ))}
                      </div>
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="py-2 font-mono text-right">{b.pieces}</td>
                <td className="py-2 font-mono text-right">{fmtSf(b.sf)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ChartCard>
    </div>
  );
}

function MaterialDrill({ data, accent }) {
  const [search, setSearch] = useState("");
  const items = useMemo(() => {
    if (!data.top_items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.top_items;
    return data.top_items.filter(
      (it) => (it.item || "").toLowerCase().includes(q) || (it.name || "").toLowerCase().includes(q)
    );
  }, [data, search]);
  return (
    <div className="space-y-5">
      <ChartCard title="Cost by Category" icon={<Package size={12} />}>
        <ResponsiveContainer width="100%" height={Math.max(180, (data.categories?.length || 1) * 24 + 40)} minWidth={0} minHeight={0}>
          <BarChart data={data.categories} layout="vertical" margin={{ top: 5, right: 30, left: 120, bottom: 5 }}>
            <CartesianGrid stroke="#1F2937" horizontal={false} />
            <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 10 }} tickFormatter={(v) => `$${fmt(v)}`} />
            <YAxis dataKey="category" type="category" stroke="#94A3B8" tick={{ fontSize: 10 }} width={120} />
            <Tooltip contentStyle={tooltipStyle} formatter={(v) => fmtMoney(v)} />
            <Bar dataKey="cost" fill={accent} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Top Items by Spend" icon={<Package size={12} />}>
        <input
          data-testid="drill-item-search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search items…"
          className="bg-[#12141D] border border-border px-3 py-1.5 text-xs mb-3 focus:outline-none focus:border-[#00D4FF]"
        />
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <tr>
              <th className="text-left py-2">Item</th>
              <th className="text-left py-2">Category</th>
              <th className="text-right py-2">Units</th>
              <th className="text-right py-2">Cost</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="py-2 font-mono" style={{ color: accent }}>{it.item}</td>
                <td className="py-2 text-xs">{it.name}</td>
                <td className="py-2 font-mono text-right">{fmt(it.units)}</td>
                <td className="py-2 font-mono text-right">{fmtMoney(it.cost)}</td>
              </tr>
            ))}
            {!items.length && (
              <tr><td colSpan={4} className="py-6 text-center text-muted-foreground text-xs uppercase">No items match</td></tr>
            )}
          </tbody>
        </table>
      </ChartCard>
    </div>
  );
}

function PiecesDrill({ data, accent }) {
  return (
    <div className="space-y-5">
      <ChartCard title="Pieces by Bed" icon={<MapPin size={12} />}>
        <ResponsiveContainer width="100%" height={Math.max(180, (data.by_bed?.length || 1) * 30 + 40)} minWidth={0} minHeight={0}>
          <BarChart data={data.by_bed} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
            <CartesianGrid stroke="#1F2937" horizontal={false} />
            <XAxis type="number" stroke="#94A3B8" tick={{ fontSize: 10 }} />
            <YAxis dataKey="bed" type="category" stroke="#94A3B8" tick={{ fontSize: 10 }} width={80} />
            <Tooltip contentStyle={tooltipStyle} />
            <Bar dataKey="pieces" fill={accent} />
          </BarChart>
        </ResponsiveContainer>
      </ChartCard>

      <ChartCard title="Details" icon={<MapPin size={12} />}>
        <table className="w-full text-sm">
          <thead className="text-[10px] uppercase tracking-widest text-[#94A3B8]">
            <tr>
              <th className="text-left py-2">Bed</th>
              <th className="text-right py-2">Pieces</th>
              <th className="text-right py-2">Jobs</th>
              <th className="text-right py-2">Total SF</th>
            </tr>
          </thead>
          <tbody>
            {data.by_bed?.map((b, i) => (
              <tr key={i} className="border-t border-border/40">
                <td className="py-2 font-mono" style={{ color: accent }}>{b.bed}</td>
                <td className="py-2 font-mono text-right">{b.pieces}</td>
                <td className="py-2 font-mono text-right">{b.jobs}</td>
                <td className="py-2 font-mono text-right">{fmtSf(b.sf)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </ChartCard>
    </div>
  );
}
